import PropTypes from "prop-types"
import React, { useEffect, useRef } from "react"

import { Switch, BrowserRouter as Router } from "react-router-dom"
import { connect } from "react-redux"

// Import Routes all
import { userRoutes, authRoutes } from "./routes/allRoutes"

// Import all middleware
import Authmiddleware from "./routes/middleware/Authmiddleware"

// Permission system
import { PermissionProvider } from "./API/PermissionContext"

// layouts Format
import VerticalLayout from "./components/VerticalLayout/"
import HorizontalLayout from "./components/HorizontalLayout/"
import NonAuthLayout from "./components/NonAuthLayout"

// Import scss
import "./assets/scss/theme.scss"

import fakeBackend from "./helpers/AuthType/fakeBackend"

// Activating fake backend
fakeBackend()

/**
 * FIX: Idle/visibility detector to prevent UI freeze after idle time.
 * When the browser tab is hidden for 5+ minutes and comes back visible,
 * we check if the auth token is still valid. If not, redirect to login
 * instead of leaving the UI in a frozen/unresponsive state.
 */
const useIdleDetector = () => {
  const lastActivityRef = useRef(Date.now())
  const IDLE_THRESHOLD_MS = 5 * 60 * 1000 // 5 minutes

  useEffect(() => {
    // Track user activity
    const updateActivity = () => {
      lastActivityRef.current = Date.now()
    }

    // When tab becomes visible again, check if we've been idle too long
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        const idleTime = Date.now() - lastActivityRef.current

        if (idleTime > IDLE_THRESHOLD_MS) {
          console.warn(`⏰ Tab was idle for ${Math.round(idleTime / 1000 / 60)}min — checking session...`)

          // Check if auth token still exists
          try {
            const userData = localStorage.getItem("authUser")
            if (!userData) {
              console.warn("🔒 No auth token found after idle — redirecting to login")
              if (!window.location.pathname.includes('/login')) {
                window.location.href = "/login"
              }
              return
            }

            // Token exists — do a lightweight ping to verify it's still valid
            const user = JSON.parse(userData)
            if (user && user.token) {
              const API_URL = process.env.REACT_APP_DATABASEURL || "http://nextgen-dairy.receptumelogic.com/api/public/api/"
              fetch(API_URL + "accesser", {
                method: "GET",
                headers: { "Authorization": `Bearer ${user.token}` },
                signal: AbortSignal.timeout(5000), // 5s max
              }).then(resp => {
                if (resp.status === 401) {
                  console.warn("🔒 Token expired after idle — redirecting to login")
                  localStorage.removeItem("authUser")
                  localStorage.removeItem("dairy_role")
                  window.location.href = "/login"
                } else {
                  console.log("✅ Session still valid after idle")
                  lastActivityRef.current = Date.now()
                }
              }).catch(() => {
                // Network error — don't redirect, just log
                console.warn("⚠️ Could not verify session — server may be unreachable")
                lastActivityRef.current = Date.now()
              })
            }
          } catch (e) {
            console.error("Error checking idle session:", e)
          }
        } else {
          // Update activity timestamp
          lastActivityRef.current = Date.now()
        }
      }
    }

    // Listen for user activity (mouse, keyboard, touch, scroll)
    const events = ['mousedown', 'keydown', 'touchstart', 'scroll']
    events.forEach(evt => window.addEventListener(evt, updateActivity, { passive: true }))
    document.addEventListener('visibilitychange', handleVisibilityChange)

    return () => {
      events.forEach(evt => window.removeEventListener(evt, updateActivity))
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [])
}


const App = props => {
  // FIX: Activate idle detector to prevent UI freeze
  useIdleDetector()

  function getLayout() {
    let layoutCls = VerticalLayout
    switch (props.layout.layoutType) {
      case "horizontal":
        layoutCls = HorizontalLayout
        break
      default:
        layoutCls = VerticalLayout
        break
    }
    return layoutCls
  }

  const Layout = getLayout()
  return (
    <React.Fragment>
      <PermissionProvider>
      <Router>
        <Switch>
          {authRoutes.map((route, idx) => (
            <Authmiddleware
              path={route.path}
              layout={NonAuthLayout}
              component={route.component}
              key={idx}
              isAuthProtected={false}
            />
          ))}

          {userRoutes.map((route, idx) => (
            <Authmiddleware
              path={route.path}
              layout={Layout}
              component={route.component}
              key={idx}
              isAuthProtected={true}
              moduleId={route.moduleId}
              action={route.action}
              exact
            />
          ))}
        </Switch>
      </Router>
      </PermissionProvider>
    </React.Fragment>
  )
}

App.propTypes = {
  layout: PropTypes.any,
}

const mapStateToProps = state => {
  return {
    layout: state.Layout,
  }
}

export default connect(mapStateToProps, null)(App)
